import React, { forwardRef } from "react";
import styled from "styled-components";
import * as Dialog from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes } from "react-icons/fa";

// Overlay styles
const StyledOverlay = styled(Dialog.Overlay)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(6px);
  z-index: 100;
`;

// Content styles
const StyledContent = styled(Dialog.Content)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90vw;
  max-width: 800px;
  max-height: 85vh;
  background: var(--background-secondary);
  border-radius: var(--border-radius);
  padding: 0;
  z-index: 101;
  overflow: hidden;
  box-shadow: 0 12px 40px -5px rgba(0, 0, 0, 0.5),
    0 0 15px rgba(212, 175, 55, 0.1);
  border: 1px solid rgba(212, 175, 55, 0.2);

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: #d4af37;
  }
`;

// Modal header styles
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(212, 175, 55, 0.15);
  position: relative;

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 10%;
    right: 10%;
    height: 1px;
    background: linear-gradient(
      90deg,
      rgba(212, 175, 55, 0) 0%,
      rgba(212, 175, 55, 0.5) 50%,
      rgba(212, 175, 55, 0) 100%
    );
  }

  h2 {
    font-family: "Playfair Display", serif;
    font-size: 1.5rem;
    margin: 0;
    color: #d4af37;
  }
`;

// Custom close icon with increased visibility
const XIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

// Close button styles
const CloseButton = styled(Dialog.Close)`
  background-color: transparent;
  border: none;
  color: #d4af37;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  transition: all 0.2s ease;
  z-index: 30;
  position: relative;

  &:hover {
    transform: scale(1.15);
  }

  svg {
    font-size: 1.2rem;
    display: block;
    width: 20px;
    height: 20px;
    stroke-width: 2;
  }
`;

// Modal body styles
const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  max-height: calc(85vh - 160px);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(212, 175, 55, 0.3);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(212, 175, 55, 0.5);
  }
`;

// Modal footer styles
const ModalFooter = styled.div`
  padding: 1.25rem 1.5rem;
  border-top: 1px solid rgba(212, 175, 55, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  position: relative;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 10%;
    right: 10%;
    height: 1px;
    background: linear-gradient(
      90deg,
      rgba(212, 175, 55, 0) 0%,
      rgba(212, 175, 55, 0.5) 50%,
      rgba(212, 175, 55, 0) 100%
    );
  }
`;

// Animation variants for framer-motion
const contentVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 10 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      damping: 30,
      stiffness: 400,
      duration: 0.3,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 10,
    transition: { duration: 0.2, ease: "easeInOut" },
  },
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.25 } },
  exit: { opacity: 0, transition: { duration: 0.2, ease: "easeInOut" } },
};

// Animated components using Framer Motion
const AnimatedContent = forwardRef(({ children, ...props }, ref) => (
  <StyledContent ref={ref} {...props}>
    {children}
  </StyledContent>
));

// Main Modal component
const Modal = ({ isOpen, onClose, title, children, footer, ...props }) => {
  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <AnimatePresence>
        {isOpen && (
          <>
            <Dialog.Portal forceMount>
              <motion.div
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={overlayVariants}
              >
                <StyledOverlay />
              </motion.div>

              <motion.div
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={contentVariants}
              >
                <AnimatedContent {...props}>
                  <ModalHeader>
                    <Dialog.Title>{title}</Dialog.Title>
                    <CloseButton aria-label="Close dialog">
                      <XIcon />
                    </CloseButton>
                  </ModalHeader>

                  <ModalBody>{children}</ModalBody>

                  {footer && <ModalFooter>{footer}</ModalFooter>}
                </AnimatedContent>
              </motion.div>
            </Dialog.Portal>
          </>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
};

export default Modal;
